/** The alert center's context and the shared summary query (see AlertCenter.tsx). */
import { useQuery } from "@tanstack/react-query";
import { createContext, useContext } from "react";

import { api } from "@/api/client";

import { useAlertsEnabled } from "./hooks";
import { SUMMARY_KEY, type AlertItem, type InboxSummary } from "./inbox";

export const MAX_CARDS = 3;
export const FALLBACK_MS = 60_000;

export interface Center {
  enabled: boolean;
  summary: InboxSummary | undefined;
  cards: AlertItem[];
  hidden: number; // cards waiting beyond the 3 shown
  popup: AlertItem[] | null;
  bellOpen: boolean;
  setBellOpen: (open: boolean) => void;
  closePopup: () => void;
  dismiss: (item: AlertItem) => void;
  read: (ids: number[]) => void;
  readAll: (upTo?: number, group?: string) => void;
  open: (item: AlertItem) => void;
  snooze: (item: AlertItem, minutes?: number) => void;
}

export const CenterContext = createContext<Center | null>(null);

const OFF: Center = {
  enabled: false,
  summary: undefined,
  cards: [],
  hidden: 0,
  popup: null,
  bellOpen: false,
  setBellOpen: () => undefined,
  closePopup: () => undefined,
  dismiss: () => undefined,
  read: () => undefined,
  readAll: () => undefined,
  open: () => undefined,
  snooze: () => undefined,
};

export function useAlertCenter(): Center {
  return useContext(CenterContext) ?? OFF;
}

/** The summary query, shared by the bell, the menu badge and the history. */
export function useInboxSummary(enabled = true) {
  const on = useAlertsEnabled() && enabled;
  return useQuery({
    queryKey: SUMMARY_KEY,
    queryFn: ({ signal }) => api.get("/me/inbox/summary", { signal }),
    enabled: on,
    refetchInterval: FALLBACK_MS,
    staleTime: 15_000,
  });
}
